import { Descriptions, Space, Spin } from 'antd'
import HelpIcon from '../HelpIcon'
import { CIMSBasicFieldProps, CIMSBasicFieldsProps } from './types'
import { alphabeticalCompare } from 'shared'
import { useItemCallback } from './hooks/useItemCallback'
import { getDeepestString } from 'utils/getReactNodeLength'
import { HelpMessage } from 'utils/constants'
import CopyPasteIcon from '../CopyPasteIcon'

/**
 * wraps the {@link Descriptions} component from Ant Design to help list named
 * fields associated with a record
 *
 * this is good for table expand sections
 *
 * @param options {@link CIMSBasicFieldsProps}
 */
const CIMSBasicFields: React.FC<CIMSBasicFieldsProps> = ({
  fields,
  children,
  title,
  concise,
  loading,
  sort,
}) => {
  fields.sort((a, b) => {
    if (!sort) return 0
    const A = a.type === 'payItems' ? 'Pay Items' : getDeepestString(a.label)
    const B = b.type === 'payItems' ? 'Pay Items' : getDeepestString(b.label)
    return alphabeticalCompare(A, B)
  })
  const item = useItemCallback()
  const reduced = fields
    .reduce((fields, field) => {
      if (field.hidden) return fields
      if (concise) {
        field.type = field.type ?? 'default'
        if (field.type === 'date' && !field.date) return fields
        else if (field.type === 'default' && !field.children) return fields
        else if (field.type === 'externalLink' && !field.linkLabel)
          return fields
        else if (
          field.type === 'externalLinks' &&
          field.links.every(({ label }) => !label)
        )
          return fields
      }
      fields.push(field)
      return fields
    }, Array<CIMSBasicFieldProps>())
    .map(({ copyable, ...field }) => {
      const help =
        field.type === 'payItems'
          ? HelpMessage.PayItemsBasicField(field.object)
          : field.help
      const label = field.type === 'payItems' ? 'Pay Items' : field.label
      return (
        <Descriptions.Item
          key={Math.random().toString()}
          label={
            <CopyPasteIcon copyable={copyable}>
              <HelpIcon text={help}>
                <Spin size="small" spinning={field.loading ?? false}>
                  {label}
                </Spin>
              </HelpIcon>
            </CopyPasteIcon>
          }
          labelStyle={{ verticalAlign: 'middle' }}
          contentStyle={{ verticalAlign: 'middle' }}
        >
          {item(field)}
        </Descriptions.Item>
      )
    })

  return (
    <Spin spinning={loading ?? false}>
      <Space key={`${title}-basic-fields`} size="middle" direction="vertical">
        {reduced.length > 0 && (
          <Descriptions title={title} bordered column={1} size="small">
            {reduced}
          </Descriptions>
        )}
        {children}
      </Space>
    </Spin>
  )
}

export default CIMSBasicFields
